/**
 * The single door to Hyperliquid's public info API.
 *
 * The endpoint is public and CORS-open (`access-control-allow-origin: *`), so
 * the browser calls it directly rather than proxying through our backend.
 *
 * Four call sites used to hand-roll this same POST with subtly different
 * behaviour: three different timeouts, two spellings of the content-type
 * header, two error messages, and only one of them guarded `AbortSignal.any`
 * before calling it. They now share this.
 */

const HYPERLIQUID_INFO_URL = 'https://api.hyperliquid.xyz/info';

/** Used when a caller expresses no preference. */
const DEFAULT_TIMEOUT_MS = 5_000;

export interface HlInfoOptions {
    /** Caller's own cancellation, combined with the timeout below. */
    signal?: AbortSignal;
    timeoutMs?: number;
}

export async function hlInfo<T>(
    body: unknown,
    { signal, timeoutMs = DEFAULT_TIMEOUT_MS }: HlInfoOptions = {},
): Promise<T> {
    const timeout = AbortSignal.timeout(timeoutMs);
    // Fall back to the timeout alone rather than the caller's signal: the
    // timeout is the safety net, and losing it would let a request hang.
    const abort =
        signal && typeof AbortSignal.any === 'function'
            ? AbortSignal.any([signal, timeout])
            : timeout;

    const response = await fetch(HYPERLIQUID_INFO_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal: abort,
    });
    if (!response.ok) {
        throw new Error(`hyperliquid info ${response.status}`);
    }
    return (await response.json()) as T;
}
